import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useLocation } from 'react-router-dom';
import { useUserSession } from '../../contexts/UserSessionContext';
import { DEFAULT_TIME_RANGE } from '../../utils/timeRange';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { useDashboardCharts } from './useDashboardCharts';
import { tabIdFromPathname } from './dashboardTabConfig';
import { shouldShowCloudSpend } from '../../utils/dashboardCloudSpend';

const DashboardContext = createContext(null);

export function DashboardProvider({ children }) {
  const { keycloak } = useKeycloak();
  const { isAdmin, rolesLoaded } = useUserSession();
  const location = useLocation();
  const activeTab = tabIdFromPathname(location.pathname);

  const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
  const [showAll, setShowAll] = useState(false);
  const [recentActivityPage, setRecentActivityPage] = useState(1);
  const [recentActivityPageSize] = useState(5);

  const handleTimeRangeChange = useCallback((nextRange) => {
    setTimeRange(nextRange);
    setRecentActivityPage(1);
  }, []);

  const stats = useDashboardStats({
    keycloak,
    rolesLoaded,
    isAdmin,
    showAll,
    timeRange,
    recentActivityPage,
    recentActivityPageSize,
    activeTab,
  });

  const showCloudSpend = useMemo(
    () => shouldShowCloudSpend(stats.statistics?.token_usage),
    [stats.statistics?.token_usage],
  );

  const charts = useDashboardCharts({
    timeRange,
    statistics: stats.statistics,
    recentActivityTotal: stats.recentActivityTotal,
    requestsData: stats.requestsData,
    tokenUsageData: stats.tokenUsageData,
    cloudSpendData: stats.cloudSpendData,
    assistantUsageData: stats.assistantUsageData,
    modelLatencyData: stats.modelLatencyData,
    onTimeRangeChange: handleTimeRangeChange,
  });

  const value = useMemo(() => ({
    keycloak,
    isAdmin,
    rolesLoaded,
    activeTab,
    timeRange,
    setTimeRange: handleTimeRangeChange,
    showAll,
    setShowAll,
    recentActivityPage,
    setRecentActivityPage,
    recentActivityPageSize,
    showCloudSpend,
    ...stats,
    ...charts,
  }), [
    keycloak,
    isAdmin,
    rolesLoaded,
    activeTab,
    timeRange,
    handleTimeRangeChange,
    showAll,
    recentActivityPage,
    recentActivityPageSize,
    showCloudSpend,
    stats,
    charts,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return ctx;
}
